import {
  DISCOUNT_TYPE,
  PROMOTION_ACTIONS,
  PROMOTION_CONDITION_TYPES,
  PROMOTION_TYPES,
} from '../types/promotionTypes';
import { v4 as uuidv4 } from 'uuid';
import { createFreeCartItem } from './cartItemPayload';
import { calculateSubTotal } from '../../../helpers/itemCalculations';

const orderPromotionTypes = [
  PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ORDER,
  PROMOTION_ACTIONS.VALUE_DISCOUNT_ON_ORDER,
];

/**
 * Appends category details to a cart item based on its ID.
 * @param cartItem - The cart item to append category details to.
 * @param categories - An array of categories containing items.
 * @returns The updated cart item with category details.
 */
function appendCategoryDetails(cartItem: any, categories: any[]) {
  let newCartItem: any = null;
  categories.map((category) => {
    category.items.map((item: any) => {
      if (item.id.toString() === cartItem.id.toString()) {
        newCartItem = {
          ...cartItem,
          category_id: category.id,
          category_name: category.name,
        };
      } else if (item.items && item.items.length) {
        //grouped items
        item.items.map((subItem: any) => {
          if (subItem.id.toString() === cartItem.id.toString()) {
            newCartItem = {
              ...cartItem,
              category_id: category.id,
              category_name: category.name,
            };
          }
        });
      }
    });
  });
  return newCartItem;
}

/**
 * Duplicates cart items based on their count.
 * @param cartItems - An array of cart items.
 * @returns The updated array of cart items with duplicated items.
 */
function duplicateCartItems(cartItems: any[]) {
  let duplicatedCartItems: any[] = [];
  cartItems.forEach((cartItem) => {
    // Calculate per-item subtotal once, handling edge cases
    const newSubTotal =
      cartItem.count > 0
        ? cartItem.sub_total / cartItem.count
        : cartItem.sub_total;

    if (cartItem?.count > 1) {
      for (let i = 0; i < cartItem.count; i++) {
        const duplicatedItem = { ...cartItem };
        duplicatedItem.temp_uuid = uuidv4(); // Add UUID to the item
        duplicatedItem.count = 1;
        duplicatedItem.sub_total = newSubTotal;
        duplicatedCartItems.push(duplicatedItem);
      }
    } else {
      const item = { ...cartItem };
      item.temp_uuid = uuidv4(); // Add UUID to the item
      duplicatedCartItems.push(item);
    }
  });
  return duplicatedCartItems;
}

function remergeCart(cartItems: any[]) {
  const cartIdGroups = new Map();

  // Group items by cart_id
  cartItems.forEach((cartItem) => {
    const cartId = cartItem.cart_id;
    if (!cartIdGroups.has(cartId)) {
      cartIdGroups.set(cartId, []);
    }
    cartIdGroups.get(cartId).push(cartItem);
  });

  // Create merged items
  const mergedCartItems: any[] = [];
  cartIdGroups.forEach((items, cartId) => {
    const firstItem = { ...items[0] };
    delete firstItem.temp_uuid;
    firstItem.count = items.length;
    firstItem.sub_total = items.reduce(
      (total: number, item: any) => total + item.sub_total,
      0
    );
    mergedCartItems.push(firstItem);
  });

  return mergedCartItems;
}

/**
 * Finds cart subtotal
 * @param cartItems - An array of cart items.
 * @returns The updated subtotal of the cart items
 */
function findCartSubtotal(cartItems: any[]) {
  return cartItems.reduce((subtotal, item) => {
    return subtotal + item.sub_total;
  }, 0);
}

//-----PROMOTION HELPER FUNCTIONS-----

/**
 * Sorts promotions by priority
 * @param availablePromotions - An array of available promotions.
 * @returns sorted promotions by priority
 */
function sortPromotions(availablePromotions: any[]) {
  const sortedPromotions = availablePromotions.sort(
    (a, b) => a.priority - b.priority
  );
  return sortedPromotions;
}

function findCartAppliedPromotions(cart: any[], promotions: any[]) {
  const appliedPromotions: any[] = [];
  cart.forEach((cartItem) => {
    const promotion = promotions.find(
      (promotion: any) => promotion.id === cartItem.applied_promotion_id
    );
    if (promotion && !appliedPromotions.includes(promotion)) {
      appliedPromotions.push(promotion);
    }
  });
  return appliedPromotions;
}

//--------------------BASIC PROMO CALCULATIONS
function applyPercentageDiscount(cartItems: any[], promotion: any) {
  let itemTotal = 0;
  let maxDiscount = promotion.action.max_discount_value ?? 0;
  let discountValue = promotion.action.value;
  let totalDiscount = 0;
  return cartItems.map((item) => {
    if (
      item.applied_promotion_id &&
      item.applied_promotion_priority <= promotion.priority
    ) {
      return item;
    }
    const itemTotalBeforePromotion = item.sub_total;
    itemTotal = promotion.action.price_modifiers
      ? item.sub_total
      : item.unit_price;
    totalDiscount = item.sub_total - (itemTotal * discountValue) / 100;
    totalDiscount =
      maxDiscount > 0 && totalDiscount > maxDiscount
        ? maxDiscount
        : totalDiscount;
    return {
      ...item,
      itemTotalBeforePromotion: itemTotalBeforePromotion,
      sub_total: totalDiscount,
      applied_promotion_id: promotion.id,
      applied_promotion_priority: promotion.priority,
    };
  });
}

function applyValueDiscount(cartItems: any[], promotion: any) {
  // Early validation
  if (!promotion?.action?.value) {
    return cartItems;
  }

  // Pre-calculate common values
  const maxDiscount = promotion.action.max_discount_value;
  let baseDiscountValue = promotion.action.value;
  const bundleItemCount = promotion.action.include.length || 1;
  const isBundleDiscount =
    promotion.condition.bundle_discount || promotion.action.bundle_discount;
  const bundleDiscountValue = isBundleDiscount
    ? (promotion.action.value / cartItems.length) *
      (cartItems.length / bundleItemCount)
    : baseDiscountValue;
  if (
    promotion.condition.type ===
    PROMOTION_CONDITION_TYPES.BUYS_FOR_SPECIFIC_AMOUNT
  ) {
    baseDiscountValue = promotion.action.value / cartItems.length;
  }

  // Process items
  return cartItems.map((item) => {
    // Skip items with higher priority promotions
    if (
      item.applied_promotion_id &&
      item.applied_promotion_priority <= promotion.priority
    ) {
      return item;
    }

    // Calculate discount value based on type
    const discountValue = isBundleDiscount
      ? bundleDiscountValue
      : baseDiscountValue;

    // Calculate new total
    let newItemTotal;
    if (discountValue > item.sub_total) {
      return item;
    } else if (promotion.action.price_modifiers) {
      newItemTotal = item.sub_total - discountValue;
    } else {
      const modifierTotal = item.sub_total - item.unit_price;
      newItemTotal =
        discountValue > item.unit_price
          ? modifierTotal
          : item.sub_total - discountValue;
    }
    // Apply minimum price check
    newItemTotal = Math.max(0, newItemTotal);

    // Apply maximum discount check
    if (maxDiscount > 0 && newItemTotal > maxDiscount) {
      newItemTotal = maxDiscount;
    }

    // Return updated item
    return {
      ...item,
      sub_total: newItemTotal,
      itemTotalBeforePromotion: item.sub_total,
      applied_promotion_id: promotion.id,
      applied_promotion_priority: promotion.priority,
    };
  });
}

/**
 * Finds promotion applicable items in the cart based on the promotion's include and exclude criteria.
 * @param cartItems - An array of cart items.
 * @param promotionAppliableItems - An array of promotion applicable items.
 * @returns The array of applicable items for the promotion.
 */
function findPromotionApplicableItems(
  cartItems: any[],
  promotionAppliableItems: any[]
) {
  const applicableItems: any[] = [];
  if (promotionAppliableItems && promotionAppliableItems.length) {
    promotionAppliableItems.forEach((item) => {
      if (item.field === 'item_id') {
        const items = cartItems.filter(
          (cartItem) => cartItem.id.toString() === item.id.toString()
        );
        applicableItems.push(...items);
      } else {
        const items = cartItems.filter(
          (cartItem) => cartItem.category_id.toString() === item.id.toString()
        );
        applicableItems.push(...items);
      }
    });
  } else {
    return cartItems;
  }
  return applicableItems;
}

function findPromotionUnapplicableItems(
  cartItems: any[],
  applicableItems: any[]
) {
  return cartItems.filter((cartItem) => {
    return !applicableItems.includes(cartItem);
  });
}

function combineDiscountedAndNonApplicableItems(
  discountedItems: any[],
  nonapplicableItems: any[]
): any[] {
  return [...discountedItems, ...nonapplicableItems];
}

/**
 * Checks if all items in the applicable items are in the cart
 * @param applicableItems - An array of applicable items
 * @param cart - The current cart
 * @returns true if all items in the applicable items are in the cart, false otherwise
 */
function checkBundledPromotionCondition(
  promotionIncludeItems: any[],
  cart: any[]
) {
  if (promotionIncludeItems.length === 0) {
    return false;
  }
  const appliableItemsItemIds = promotionIncludeItems.map((item) => item.id);
  // check if all items in appliableItemsItemIds are in cart
  return appliableItemsItemIds.every((itemId) =>
    cart.some((cartItem) => cartItem.id.toString() === itemId.toString())
  );
}

/**
 * Finds the applicable items for a bundled discount promotion
 * @param promotionIncludeItems - An array of promotion include items
 * @param cart - The current cart
 * @returns An object containing the applicable and non-applicable items
 */
function bundleDiscountItemsData(promotionIncludeItems: any[], cart: any[]) {
  // Get all required item IDs
  const requiredItemIds = promotionIncludeItems.map((item) => item.id);

  // Create a map to track how many of each item we have in cart
  const itemCounts = new Map();
  requiredItemIds.forEach((id) => {
    itemCounts.set(id, 0);
  });

  // Count occurrences of each required item in cart, considering item counts
  cart.forEach((cartItem) => {
    if (requiredItemIds.includes(cartItem.id.toString())) {
      itemCounts.set(
        cartItem.id.toString(),
        (itemCounts.get(cartItem.id.toString()) || 0) + (cartItem.count || 1)
      );
    }
  });

  // Find minimum count of any required item - this tells us how many complete sets we have
  const minCount = Math.min(...Array.from(itemCounts.values()));

  // If we don't have at least one complete set, return empty array for applicable items
  // and all items as non-applicable
  if (minCount === 0) {
    return {
      applicableItems: [],
      nonapplicableItems: [...cart],
    };
  }

  // For each complete set, add the items to applicable items
  const applicableItems: any[] = [];
  const usedItemIds = new Set(); // Track which items we've used by temp_uuid

  // For each complete set
  for (let i = 0; i < minCount; i++) {
    // For each required item in the set
    for (const id of requiredItemIds) {
      // Find an item with this ID that hasn't been used
      const cartItem = cart.find(
        (item) => item.id.toString() === id && !usedItemIds.has(item.temp_uuid)
      );

      if (cartItem) {
        // Create a copy of the item with count 1
        const itemCopy = { ...cartItem, count: 1 };
        applicableItems.push(itemCopy);
        usedItemIds.add(cartItem.temp_uuid);
      }
    }
  }

  // Create nonapplicableItems by filtering out used items
  const nonapplicableItems = cart.filter(
    (item) => !usedItemIds.has(item.temp_uuid)
  );

  return {
    applicableItems,
    nonapplicableItems,
  };
}

/**
 * Applies a discount to cart items based on include/exclude rules
 * @param cart - Current cart items
 * @param promotion - The promotion containing discount rules
 * @param discountFunction - Function to apply the specific type of discount
 * @returns Updated cart with discounts applied
 */
function applyDiscount(
  cart: any[],
  promotion: any,
  discountFunction: (items: any[], promotion: any) => any[]
): any[] {
  const bundledPromotion =
    promotion.action?.bundle_discount ||
    promotion.condition?.bundle_discount ||
    false;
  // Case 1: No inclusion/exclusion rules - apply to all items
  if (
    promotion.action.include.length === 0 &&
    promotion.action.exclude.length === 0
  ) {
    return discountFunction(cart, promotion);
  }

  // Case 2: Has inclusion rules only
  if (
    promotion.action.include.length &&
    promotion.action.exclude.length === 0
  ) {
    const applicableItems = findPromotionApplicableItems(
      cart,
      promotion.action.include
    );
    if (bundledPromotion) {
      const isBundleConditionMet = checkBundledPromotionCondition(
        promotion.action.include,
        cart
      );
      if (isBundleConditionMet) {
        const bundleDiscountItemsDetails = bundleDiscountItemsData(
          promotion.action.include,
          cart
        );
        const bundleDiscountItems = discountFunction(
          bundleDiscountItemsDetails.applicableItems,
          promotion
        );
        return combineDiscountedAndNonApplicableItems(
          bundleDiscountItems,
          bundleDiscountItemsDetails.nonapplicableItems
        );
      } else {
        return cart;
      }
    }
    const nonapplicableItems = findPromotionUnapplicableItems(
      cart,
      applicableItems
    );
    const discountedApplicableItems = discountFunction(
      applicableItems,
      promotion
    );
    return combineDiscountedAndNonApplicableItems(
      discountedApplicableItems,
      nonapplicableItems
    );
  }

  // Case 3: Has exclusion rules only
  if (
    promotion.action.exclude.length &&
    promotion.action.include.length === 0
  ) {
    const nonapplicableItems = findPromotionApplicableItems(
      cart,
      promotion.action.exclude
    );
    const applicableItems = findPromotionUnapplicableItems(
      cart,
      nonapplicableItems
    );
    const discountedNonapplicableItems = discountFunction(
      applicableItems,
      promotion
    );
    return combineDiscountedAndNonApplicableItems(
      discountedNonapplicableItems,
      nonapplicableItems
    );
  } else {
    // Case 4: Has both inclusion and exclusion rules
    let applicableItems = findPromotionApplicableItems(
      cart,
      promotion.action.include
    );

    const unapplicableItems = findPromotionApplicableItems(
      cart,
      promotion.action.exclude
    );
    applicableItems = applicableItems.filter(
      (item) => !unapplicableItems.includes(item)
    );
    if (bundledPromotion) {
      const isBundleConditionMet = checkBundledPromotionCondition(
        promotion.action.include,
        cart
      );
      if (isBundleConditionMet) {
        const bundleDiscountItemsDetails = bundleDiscountItemsData(
          promotion.action.include,
          cart
        );
        const bundleDiscountItems = discountFunction(
          bundleDiscountItemsDetails.applicableItems,
          promotion
        );
        return combineDiscountedAndNonApplicableItems(
          bundleDiscountItems,
          bundleDiscountItemsDetails.nonapplicableItems
        );
      } else {
        return cart;
      }
    }
    const discountedNonapplicableItems = discountFunction(
      applicableItems,
      promotion
    );
    const nonapplicableItems = findPromotionUnapplicableItems(
      cart,
      applicableItems
    );
    return combineDiscountedAndNonApplicableItems(
      discountedNonapplicableItems,
      nonapplicableItems
    );
  }
}

/**
 * Applies basic promotions like percentage or value discounts
 * @param promotionAppliedCart - Current cart with any previously applied promotions
 * @param promotion - The promotion to apply
 * @param orderPromotion - Array of order-level promotions
 * @returns Updated cart and order promotions
 */
function applyBasicPromotion(
  promotionAppliedCart: any[],
  promotion: any,
  orderPromotion: any[]
) {
  const isDiscountOnOrder =
    promotion.action.type === PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ORDER ||
    promotion.action.type === PROMOTION_ACTIONS.VALUE_DISCOUNT_ON_ORDER;

  if (isDiscountOnOrder) {
    // Handle order-level discounts
    orderPromotion.push(promotion);
  } else if (promotion.action.type === PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT) {
    // Apply percentage discount to individual items
    promotionAppliedCart = applyDiscount(
      promotionAppliedCart,
      promotion,
      applyPercentageDiscount
    );
  } else if (promotion.action.type === PROMOTION_ACTIONS.VALUE_DISCOUNT) {
    // Apply fixed value discount to individual items
    promotionAppliedCart = applyDiscount(
      promotionAppliedCart,
      promotion,
      applyValueDiscount
    );
  }
  return { promotionAppliedCart, orderPromotion };
}

//-------------------ADVANCED PROMO CALCULATIONS
function applyAdvancedDiscount(
  applicableItems: any[],
  promotion: any,
  discountFunction: (items: any[], promotion: any) => any[]
): any[] {
  const discountedItems = discountFunction(applicableItems, promotion);
  return discountedItems;
}

/**Filters items that have no promotion applied or have a lower priority promotion applied.
 * @param applicableItems - An array of items that are applicable for the promotion.
 * @param newPromotion - The new promotion to be applied.
 * @returns Item on which promotions can be applied.
 */
function filterOutPromotionAppliedItems(
  applicableItems: any[],
  newPromotion: any
) {
  return applicableItems.filter(
    (item) =>
      !item.applied_promotion_id ||
      item.applied_promotion_priority > newPromotion.priority
  );
}

/**
 * Calculates the discounted subtotal for an item based on whether price modifiers are included.
 * For items with price modifiers, applies discount directly.
 * For items without price modifiers, adds modifier price to the discount.
 * In both cases, ensures the final price doesn't exceed the item's subtotal.
 *
 * @param item - The cart item to calculate discount for
 * @param pricedModifer - Whether price modifiers should be included in the calculation
 * @param discountAmount - The amount to discount
 * @returns The discounted subtotal, capped at the item's subtotal
 */
function discountedItemSubtotal(
  item: any,
  pricedModifer: boolean,
  discountAmount: number
): number {
  //for fixed discount only
  const modifierPrice = item.sub_total - item.unit_price;

  // For items with price modifiers, use discount amount directly
  // For items without price modifiers, add modifier price to discount
  const newDiscountAmount = pricedModifer
    ? discountAmount
    : discountAmount + modifierPrice;

  // Ensure the final price is not less than the item's subtotal
  return Math.min(newDiscountAmount, item.sub_total);
}

/**
 * Finds the number of complete sets of bundle discount items in the cart.
 * @param cartItems - The cart items.
 * @param bundleItemsIds - The ids of the bundle discount items.
 * @returns The number of complete sets of bundle discount items in the cart.
 */
function findBundleDiscountSetCount(cartItems: any[], bundleItemsIds: any[]) {
  // Create a map to track items for each bundle ID
  const itemMap = new Map();

  // Initialize arrays for each bundle ID
  bundleItemsIds.forEach((id) => itemMap.set(id, []));

  // Group items by their bundle ID
  cartItems.forEach((item) => {
    if (bundleItemsIds.includes(item.id)) {
      const items = itemMap.get(item.id) || [];
      items.push(item);
      itemMap.set(item.id, items);
    }
  });

  // Check if we have at least one item for each bundle ID
  const hasAllItems = bundleItemsIds.every(
    (id) => (itemMap.get(id) || []).length > 0
  );
  if (!hasAllItems) return [];

  // Get the minimum count of items across all bundle IDs
  const minCount = Math.min(
    ...bundleItemsIds.map((id) => itemMap.get(id).length)
  );

  // Return all complete sets
  const result = [];
  for (let i = 0; i < minCount; i++) {
    const set = bundleItemsIds.map((id) => itemMap.get(id)[i]);
    result.push(set);
  }

  // Flatten the array using reduce instead of flat()
  return result.reduce((acc, curr) => [...acc, ...curr], []);
}

/**
 * Applies a fixed price promotion to the cart items.
 * @param cartItems - The cart items.
 * @param promotion - The promotion to apply.
 * @returns The updated cart items.
 */
function applyFixedPricePromotion(cartItems: any[], promotion: any) {
  //fixed price promotion on items
  const promotionAction = promotion.action;
  let applicableItems = findPromotionConditionItemsInCart(
    promotion.condition,
    cartItems
  );
  const pricedModifierItems: boolean = promotionAction.price_modifiers;
  const isBundlePromotion =
    promotion.condition?.bundle_discount ||
    promotion.action?.bundle_discount ||
    false;
  let unapplicableItems = cartItems.filter(
    (item) => !applicableItems.includes(item)
  );
  let fixedValue = parseFloat(promotionAction.value);
  if (isBundlePromotion && promotion.condition.include.length) {
    const bundleItemsCount = promotion.condition.include.length || 1;
    fixedValue = fixedValue / bundleItemsCount;
    const bundleItemIds = promotion.condition.include.map((item: any) =>
      parseInt(item.id)
    );
    const applicableBundleItems = findBundleDiscountSetCount(
      cartItems,
      bundleItemIds
    );
    applicableItems = applicableBundleItems;
    unapplicableItems = cartItems.filter(
      (item) => !applicableBundleItems.includes(item)
    );
  }
  const minQuantity = promotion.condition.min_qty;
  const maxQuantity = promotion.condition.max_qty;
  let promotionAppliedItems: any[] = [];
  const exactQuantity = minQuantity === maxQuantity ? minQuantity : 0;
  const applicableItemsCount = applicableItems.length;
  if (minQuantity > 0 && applicableItemsCount >= minQuantity) {
    if (exactQuantity > 0) {
      if (applicableItemsCount % exactQuantity === 0) {
        const factor = Math.floor(applicableItemsCount / exactQuantity);
        const itemSubTotal = (fixedValue * factor) / applicableItems.length;
        promotionAppliedItems = applicableItems.map((item) => {
          const newItemSubTotal = discountedItemSubtotal(
            item,
            pricedModifierItems,
            itemSubTotal
          );
          const isPromotionEligible = newItemSubTotal !== item.sub_total;
          return {
            ...item,
            itemTotalBeforePromotion: item.sub_total,
            sub_total: newItemSubTotal,
            applied_promotion_id: isPromotionEligible ? promotion.id : null,
            applied_promotion_priority: isPromotionEligible
              ? promotion.priority
              : null,
          };
        });
      } else {
        //split items to highest possible multiple of exact quantity
        const appliableItemsCount = Math.floor(
          applicableItemsCount / exactQuantity
        );
        const appliableItems = applicableItems.slice(
          0,
          appliableItemsCount * exactQuantity
        );
        const unappliableItems = applicableItems.slice(
          appliableItemsCount * exactQuantity
        );
        const itemSubTotal =
          (fixedValue * appliableItemsCount) / appliableItems.length;
        promotionAppliedItems = appliableItems.map((item) => {
          const newItemSubTotal = discountedItemSubtotal(
            item,
            pricedModifierItems,
            itemSubTotal
          );
          const isPromotionEligible = newItemSubTotal !== item.sub_total;
          return {
            ...item,
            itemTotalBeforePromotion: item.sub_total,
            sub_total: newItemSubTotal,
            applied_promotion_id: isPromotionEligible ? promotion.id : null,
            applied_promotion_priority: isPromotionEligible
              ? promotion.priority
              : null,
          };
        });
        promotionAppliedItems = [...promotionAppliedItems, ...unappliableItems];
      }
    } else {
      const appliableItemsCount = Math.ceil(applicableItemsCount / maxQuantity);
      const appliableItems = applicableItems.slice(
        0,
        appliableItemsCount * maxQuantity
      );
      const unappliableItems = applicableItems.slice(
        appliableItemsCount * maxQuantity
      );
      let itemSubTotal = 0;
      if (appliableItems.length > maxQuantity) {
        itemSubTotal = fixedValue / maxQuantity;
        promotionAppliedItems = appliableItems.map((item, index) => {
          if (index < maxQuantity) {
            return {
              ...item,
              itemTotalBeforePromotion: item.sub_total,
              sub_total: discountedItemSubtotal(
                item,
                pricedModifierItems,
                itemSubTotal
              ),
              applied_promotion_id: promotion.id,
              applied_promotion_priority: promotion.priority,
            };
          } else {
            return {
              ...item,
              applied_promotion_id: promotion.id,
              applied_promotion_priority: promotion.priority,
            };
          }
        });
      } else {
        itemSubTotal =
          (fixedValue * appliableItemsCount) /
          (minQuantity * appliableItemsCount);
        promotionAppliedItems = appliableItems.map((item, index) => {
          if (index % maxQuantity < minQuantity && index < maxQuantity) {
            return {
              ...item,
              itemTotalBeforePromotion: item.sub_total,
              sub_total: discountedItemSubtotal(
                item,
                pricedModifierItems,
                itemSubTotal
              ),
              applied_promotion_id: promotion.id,
              applied_promotion_priority: promotion.priority,
            };
          } else {
            return {
              ...item,
              applied_promotion_id: promotion.id,
              applied_promotion_priority: promotion.priority,
            };
          }
        });
      }

      promotionAppliedItems = [...promotionAppliedItems, ...unappliableItems];
    }
  } else {
    return cartItems;
  }
  return [...promotionAppliedItems, ...unapplicableItems];
}

const sortItemsBySubtotal = (items: any[]) => {
  return items.sort((a, b) => a.sub_total - b.sub_total);
};

/**
 * Filters and splits items based on promotion action quantity requirements
 * action items count is taken here
 * @param promotionApplicableItems - Items eligible for the promotion
 * @param promotionUnapplicableItems - Items not eligible for the promotion
 * @param promotionActionItemsQuantity - Required quantity for the promotion
 * @param qtySpecifiedInAction - Whether quantity is explicitly specified in action
 * @param cartItems - All items in cart
 * @returns Filtered applicable and unapplicable items
 */
const filterAdvancedPromoActionItems = (
  promotionApplicableItems: any[],
  promotionUnapplicableItems: any[],
  promotionActionItemsQuantity: number,
  qtySpecifiedInAction: boolean,
  cartItems: any[]
) => {
  // No items required for promotion action
  if (promotionActionItemsQuantity <= 0 && !qtySpecifiedInAction) {
    return {
      promotionApplicableItems: [],
      promotionUnapplicableItems: cartItems,
    };
  }

  // Handle cases where quantity splitting is needed
  if (promotionActionItemsQuantity > 0) {
    const { slicedItems, remainingItems } = splitItemsByQuantity(
      promotionApplicableItems,
      promotionActionItemsQuantity,
      qtySpecifiedInAction
    );
    return {
      promotionApplicableItems: slicedItems,
      promotionUnapplicableItems: [
        ...promotionUnapplicableItems,
        ...remainingItems,
      ],
    };
  }

  // Return original items if no other conditions met
  return { promotionApplicableItems, promotionUnapplicableItems };
};

/**
 * Checks if all action items are present in condition items.
 * @param actionItems - The array of action items.
 * @param conditionItems - The array of condition items.
 * @returns True if all action items are present in condition items, otherwise false.
 */
function checkAllActionItemsPresentInConditionItems(
  actionItems: any[],
  conditionItems: any[]
) {
  return actionItems.every((actionItem) =>
    conditionItems.some(
      (conditionItem) =>
        conditionItem.id.toString() === actionItem.id.toString()
    )
  );
}

/**
 * Filters out items that are excluded from the promotion.
 * @param cartItems - The array of cart items.
 * @param exclude - The array of items to exclude.
 * @returns The filtered array of cart items.
 */
const filterExcludedItems = (cartItems: any[], exclude: any[]) => {
  return cartItems.filter(
    (item) =>
      !exclude.some(
        (excludedId) => excludedId.toString() === item.id.toString()
      )
  );
};

/**
 * Finds the promotion action items based on the given promotion and cart items.
 *
 * @param promotion - The promotion object.
 * @param cartItems - The array of cart items.
 * @returns An obj of action items and non applicableItems.
 */
function findPromotionActionItems(promotion: any, cartItems: any[]) {
  const { include, exclude } = promotion.action;
  let promotionAppliableItems: any[] = [];
  let promotionUnappliableItems: any[] = [];
  let promotionActionItemsQuantity = promotion.action.quantity;
  let actionItems: any[] = [];
  let allActionItemsIncluded = false;
  const isBundlePromotion =
    promotion.condition?.bundle_discount ||
    promotion.action?.bundle_discount ||
    false;
  const conditionItems = findPromotionConditionItemsInCart(
    promotion.condition,
    cartItems
  );
  let actionItemsQuantity =
    promotion.action.quantity && promotion.action.quantity > 0
      ? promotion.action.quantity
      : 0;
  if (isBundlePromotion) {
    const bundleDiscountItemsDetails = bundleDiscountItemsData(
      promotion.condition.include,
      cartItems
    );
    const bundleConditionItemsInCart =
      bundleDiscountItemsDetails.applicableItems;
    const promotionIncludeItems = promotion.condition.include;
    const bundleCount = Math.floor(
      bundleConditionItemsInCart.length /
        promotionIncludeItems.length /
        promotion.condition.min_qty
    );
    promotionActionItemsQuantity = bundleCount;
  }
  const allActionItemsPresentInConditionItems =
    checkAllActionItemsPresentInConditionItems(
      promotion.action.include,
      conditionItems
    );

  // Check if promotion has items to include in the action
  if (promotion.action.include) {
    actionItems = findPromotionApplicableItems(
      cartItems,
      promotion.action.include
    );
  }

  // Verify if all action items are present in the cart
  if (actionItems.length) {
    allActionItemsIncluded = actionItems.every((actionItem) =>
      cartItems.includes(actionItem)
    );
  }

  const { min_qty = 0, max_qty = 0 } = promotion.condition;

  //handle qty when all action items are present in the cart and its a fixed qty promotion
  if (allActionItemsIncluded && allActionItemsPresentInConditionItems) {
    const initialActionItemsQuantity = promotion.action.quantity;
    const isFixedQtyPromotion =
      min_qty === max_qty && min_qty > 0 && max_qty > 0;
    if (isFixedQtyPromotion) {
      const cartLength = cartItems.length;
      const conditionItemCount = min_qty + initialActionItemsQuantity;
      if (cartLength < conditionItemCount) {
        const conditionIncludeItems = promotion.action?.include ?? [];
        if (conditionIncludeItems.length) {
          return {
            promotionApplicableItems: [],
            promotionUnapplicableItems: cartItems,
          };
        }
        const difference = conditionItemCount - cartLength;
        promotionActionItemsQuantity = difference;
      }
      //handle qty when all action items are present in the cart and its a fixed qty promotion

      if (cartLength % conditionItemCount === 0) {
        promotionActionItemsQuantity =
          initialActionItemsQuantity * (cartLength / conditionItemCount);
      }
    } else {
      promotionActionItemsQuantity = initialActionItemsQuantity;
    }
  }

  // Case 1: No specific include/exclude rules - apply to all eligible items
  if (include.length === 0 && exclude.length === 0) {
    // Get all items that don't have promotions applied
    promotionAppliableItems = filterOutPromotionAppliedItems(
      cartItems,
      promotion
    );
    // Find items that can't have the promotion applied
    promotionUnappliableItems = cartItems.filter(
      (item) => !promotionAppliableItems.includes(item)
    );
    // Special handling for 'buy for specific amount' condition
    if (
      promotion.condition.type ===
      PROMOTION_CONDITION_TYPES.BUYS_FOR_SPECIFIC_AMOUNT
    ) {
      promotionActionItemsQuantity =
        actionItemsQuantity || promotionAppliableItems.length;
    }
    promotionAppliableItems = sortItemsBySubtotal(promotionAppliableItems);
    return filterAdvancedPromoActionItems(
      promotionAppliableItems,
      promotionUnappliableItems,
      promotionActionItemsQuantity,
      actionItemsQuantity > 0,
      cartItems
    );
  }
  // Case 2: Has specific items to include in promotion
  else if (include.length && exclude.length === 0) {
    // Find items that match the inclusion criteria
    const promotionIncludeItems = findPromotionApplicableItems(
      cartItems,
      include
    );
    // Filter out items that already have promotions
    promotionAppliableItems = filterOutPromotionAppliedItems(
      promotionIncludeItems,
      promotion
    );
    // Items that don't match inclusion criteria are unappliable
    promotionUnappliableItems = cartItems.filter(
      (item) => !promotionIncludeItems.includes(item)
    );
    // Special handling for 'buy for specific amount' condition
    if (
      promotion.condition.type ===
      PROMOTION_CONDITION_TYPES.BUYS_FOR_SPECIFIC_AMOUNT
    ) {
      promotionActionItemsQuantity =
        actionItemsQuantity || promotionAppliableItems.length;
    }
    promotionAppliableItems = sortItemsBySubtotal(promotionAppliableItems);
    //condition when max and min quantity are present
    return filterAdvancedPromoActionItems(
      promotionAppliableItems,
      promotionUnappliableItems,
      promotionActionItemsQuantity,
      actionItemsQuantity > 0,
      cartItems
    );
  }
  // Case 3: Has items to exclude from promotion
  else {
    // Find items that match exclusion criteria
    const promotionExcludeItems = findPromotionApplicableItems(
      cartItems,
      exclude
    );
    // Items not in exclusion list are eligible
    const promotionIncludeItems = cartItems.filter(
      (item) => !promotionExcludeItems.includes(item)
    );
    // Filter out items that already have promotions
    promotionAppliableItems = filterOutPromotionAppliedItems(
      promotionIncludeItems,
      promotion
    );
    promotionAppliableItems = filterExcludedItems(
      promotionAppliableItems,
      exclude
    );
    // Items that match exclusion criteria are unappliable
    promotionUnappliableItems = cartItems.filter(
      (item) => !promotionIncludeItems.includes(item)
    );

    // Special handling for 'buy for specific amount' condition
    if (
      promotion.condition.type ===
      PROMOTION_CONDITION_TYPES.BUYS_FOR_SPECIFIC_AMOUNT
    ) {
      promotionActionItemsQuantity =
        actionItemsQuantity || promotionAppliableItems.length;
    }
    promotionAppliableItems = sortItemsBySubtotal(promotionAppliableItems);
    return filterAdvancedPromoActionItems(
      promotionAppliableItems,
      promotionUnappliableItems,
      promotionActionItemsQuantity,
      actionItemsQuantity > 0,
      cartItems
    );
  }
}

/**
 * Checks if any item in action.include is present in condition.include.
 * @param promotion - The promotion object containing condition and action.
 * @returns True if any item in action.include is present in condition.include, otherwise false.
 */
function isActionIncludeInConditionInclude(promotion: any): boolean {
  const { condition, action } = promotion;

  if (!condition.include || !action.include) {
    return false;
  }

  return action.include.some((actionItem: any) => {
    return condition.include.some(
      (conditionItem: any) =>
        conditionItem.id === actionItem.id &&
        conditionItem.field === actionItem.field
    );
  });
}

function findPromotionConditionItemsInCart(
  promotionCondition: any,
  cartItems: any[]
) {
  const conditionExcludeItems = promotionCondition.exclude || [];
  const conditionIncludeItems = promotionCondition.include || [];
  const deletedExcludedItemsCart: any[] = [];
  let excludedItems: any[] = [];
  let applicableItems: any[] = [];
  if (
    conditionExcludeItems.length === 0 &&
    conditionIncludeItems.length === 0
  ) {
    return cartItems;
  }
  if (conditionExcludeItems.length) {
    excludedItems = conditionExcludeItems.map((item: any) => {
      if (item.field === 'item_id') {
        return cartItems.filter(
          (cartItem) => cartItem.id.toString() === item.id.toString()
        );
      } else {
        return cartItems.filter(
          (cartItem) => cartItem.category_id.toString() === item.id.toString()
        );
      }
    });
    deletedExcludedItemsCart.push(
      ...cartItems.filter((item) => !excludedItems.includes(item))
    );
  } else {
    deletedExcludedItemsCart.push(...cartItems);
  }
  if (conditionIncludeItems.length) {
    conditionIncludeItems.forEach((item: any) => {
      if (item.field === 'item_id') {
        const items = deletedExcludedItemsCart.filter(
          (cartItem) => cartItem.id.toString() === item.id.toString()
        );
        applicableItems.push(...items);
      } else {
        const items = deletedExcludedItemsCart.filter(
          (cartItem) => cartItem.category_id.toString() === item.id.toString()
        );
        applicableItems.push(...items);
      }
    });
    if (conditionExcludeItems.length) {
      applicableItems = applicableItems.filter((item) => {
        const isExcluded = conditionExcludeItems.some((excludedItem: any) => {
          return (
            excludedItem.field === 'item_id' &&
            excludedItem.id.toString() === item.id.toString()
          );
        });
        return !isExcluded;
      });
    }
  } else {
    applicableItems.push(...deletedExcludedItemsCart);
  }
  return applicableItems;
}

/**
 * MAIN FUNCTION for applying free item promotions
 * Applies a free item promotion to the cart
 * @param cartItems - Current cart items
 * @param promotion - Promotion to apply
 * @param menuItems - Available menu items
 * @param freeItemCart - Current free items
 * @param freeItemsCartCopy - Copy of free items cart
 * @returns Updated free items cart
 *
 * Promotion Types:
 * 1. Buy for specific amount (e.g., spend $50 get free item)
 * 2. Buy specific items (e.g., buy 2 items get 1 free)
 *
 * Quantity Rules:
 * - Exact quantity: Must buy exact number of items
 * - Range quantity: Must buy between min and max items
 * - Price-based: Must spend minimum amount
 */
function applyFreeItem(
  cartItems: any[],
  promotion: any,
  menuItems: any[],
  freeItemCart: any[],
  freeItemsCartCopy: any[]
) {
  const promotionId = promotion.id;
  const promotionPriority = promotion.priority;
  const priceModifiers = promotion.action.price_modifiers;
  const previousFreeItemCart = freeItemsCartCopy;

  // Use Set for faster lookups
  const applicableCartSet = new Set(cartItems.map((item) => item.id));

  // Extract the ID of the free item from the promotion action
  const freeItemId = promotion.action.include[0].id;
  let freeItem: any = null;
  const promotionAction = promotion.action;
  const promotionCondition = promotion.condition;
  const conditionMinQty = promotionCondition.min_qty;
  const conditionMaxQty = promotionCondition.max_qty;
  let freeItems: any[] = [];
  let newFreeItemCart: any[] = freeItemCart ?? [];

  // Filter cart items that are eligible for this promotion
  // Items are eligible if they have no promotion or a lower priority promotion
  const appliableCart = cartItems.filter(
    (item) =>
      applicableCartSet.has(item.id) &&
      (!item.applied_promotion_id ||
        item.applied_promotion_id >= promotion.priority)
  );

  // Find items in cart that meet the promotion conditions
  const promotionConditionItemsInCart = findPromotionConditionItemsInCart(
    promotionCondition,
    appliableCart
  );

  // Use early returns for better flow control
  if (!promotionConditionItemsInCart.length) {
    return freeItemCart;
  }

  // Check if the free item is already in the cart
  const isFreeItemInFreeCart = previousFreeItemCart.some(
    (item) => item.id.toString() === freeItemId.toString()
  );

  // Handle "Buy for specific amount" promotions
  if (
    promotionCondition.type ===
    PROMOTION_CONDITION_TYPES.BUYS_FOR_SPECIFIC_AMOUNT
  ) {
    // Create new free item from menu items
    const freeMenuItem = menuItems.find(
      (item) => item.id.toString() === freeItemId.toString()
    );
    if (freeMenuItem?.id) {
      if (isFreeItemInFreeCart) {
        freeItem = previousFreeItemCart.find(
          (item) => item.id.toString() === freeItemId.toString()
        );
      } else {
        freeItem = createFreeCartItem(
          freeMenuItem,
          1,
          !priceModifiers,
          promotionId,
          promotionPriority
        );
      }
    }
    // Create the specified quantity of free items
    freeItems = Array.from({ length: promotionAction.quantity }, () => ({
      ...freeItem,
      cart_id: uuidv4(),
    }));
    if (freeItems.length) {
      newFreeItemCart.push(...freeItems);
    }
  } else {
    // Handle "Buy specific items" promotions
    // Create new free item from menu items
    const freeMenuItem = menuItems.find(
      (item) => item.id.toString() === freeItemId.toString()
    );
    let freeItemQuantity: number = 0;
    //check if the promotion is a bundle promotion
    let isBundleConditionMet: boolean = false;
    const isBundlePromotion =
      promotion.condition?.bundle_discount ||
      promotion.action?.bundle_discount ||
      false;
    if (isBundlePromotion) {
      isBundleConditionMet = checkBundledPromotionCondition(
        promotion.condition.include,
        cartItems
      );
      if (!isBundleConditionMet) {
        return freeItemCart;
      }
    }
    if (freeMenuItem?.id) {
      if (isFreeItemInFreeCart) {
        freeItem = previousFreeItemCart.find(
          (item) => item.id.toString() === freeItemId.toString()
        );
      } else {
        freeItem = createFreeCartItem(
          freeMenuItem,
          1,
          !priceModifiers,
          promotionId,
          promotionPriority
        );
      }
      // Calculate quantity of free items based on conditions
      if (conditionMinQty > 0 && conditionMaxQty > 0) {
        if (conditionMinQty === conditionMaxQty) {
          // For exact quantity requirements
          freeItemQuantity =
            Math.floor(promotionConditionItemsInCart.length / conditionMinQty) *
            promotionAction.quantity;
          //if the promotion is a bundle promotion, then we need to calculate the free item quantity based on the bundle condition
          if (isBundlePromotion) {
            const bundleDiscountItemsDetails = bundleDiscountItemsData(
              promotion.condition.include,
              cartItems
            );
            const bundleConditionItemsInCart =
              bundleDiscountItemsDetails.applicableItems;
            const promotionIncludeItems = promotion.condition.include;
            freeItemQuantity =
              Math.floor(
                Math.floor(
                  bundleConditionItemsInCart.length /
                    promotionIncludeItems.length /
                    conditionMinQty
                )
              ) * promotionAction.quantity;
          }
        } else {
          // For range-based quantity requirements
          if (promotionConditionItemsInCart.length >= conditionMinQty) {
            freeItemQuantity = promotionAction.quantity;
            if (isBundlePromotion) {
              const bundleDiscountItemsDetails = bundleDiscountItemsData(
                promotion.condition.include,
                cartItems
              );
              const bundleConditionItemsInCart =
                bundleDiscountItemsDetails.applicableItems;
              const promotionIncludeItems = promotion.condition.include;
              const bundleCount = Math.floor(
                bundleConditionItemsInCart.length / promotionIncludeItems.length
              );
              freeItemQuantity = promotionAction.quantity;
            }
          }
        }
      }
      // Create and add free items to cart
      freeItems = Array.from({ length: freeItemQuantity }, () => ({
        ...freeItem,
        cart_id: uuidv4(),
      }));
      newFreeItemCart.push(...freeItems);
    }
  }
  return newFreeItemCart;
}

/**
 * MAIN FUNCTION FOR APPLYING PROMOTION ACTIONS IN ADVANCED PROMOTIONS
 * Applies the action of a promotion to the cart items.
 * @param promotionAppliedCart - The array of cart items that have already applied promotions.
 * @param promotion - The promotion object.
 * @param orderPromotions - The array of order promotions.
 * @param conditionItems - The array of condition items.
 * @returns The array of cart items with the promotion action applied.
 */
function applyPromotionAction(
  promotionAppliedCart: any[],
  promotion: any,
  orderPromotions: any[]
): any[] {
  const promotionAction = promotion.action;
  let applicableItems: any[] = [];
  let unapplicableItems: any[] = [];

  //if the promotion is not a fixed price promotion or an order promotion, then we need to find the applicable items
  if (
    !(
      promotionAction.surcharge?.id ||
      promotionAction.type === PROMOTION_ACTIONS.PAY_FIXED_PRICE ||
      orderPromotionTypes.includes(promotionAction.type)
    )
  ) {
    const promotionActionItems = findPromotionActionItems(
      promotion,
      promotionAppliedCart
    );
    applicableItems = promotionActionItems.promotionApplicableItems;
    unapplicableItems = promotionActionItems.promotionUnapplicableItems;
  }

  switch (promotionAction.type) {
    case PROMOTION_ACTIONS.PAY_FIXED_PRICE:
      return applyFixedPricePromotion(promotionAppliedCart, promotion);
    case PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ORDER:
    case PROMOTION_ACTIONS.VALUE_DISCOUNT_ON_ORDER:
    case PROMOTION_ACTIONS.EARN_LOYALTY:
    case PROMOTION_ACTIONS.GETS_FREE_DELIVERY:
    case PROMOTION_ACTIONS.GETS_PERCENTAGE_DISCOUNT_ON_DELIVERY:
    case PROMOTION_ACTIONS.GETS_VALUE_DISCOUNT_ON_DELIVERY:
      orderPromotions.push(promotion);
      return promotionAppliedCart;
    case PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ITEM:
    case PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT:
      const percentageDiscountAppliedItem = applyAdvancedDiscount(
        applicableItems,
        promotion,
        applyPercentageDiscount
      );
      return [...percentageDiscountAppliedItem, ...unapplicableItems];
    case PROMOTION_ACTIONS.VALUE_DISCOUNT_ON_ITEM:
    case PROMOTION_ACTIONS.VALUE_DISCOUNT:
      const valueDiscountAppliedItems = applyAdvancedDiscount(
        applicableItems,
        promotion,
        applyValueDiscount
      );
      return [...valueDiscountAppliedItems, ...unapplicableItems];
    default:
      return promotionAppliedCart;
  }
}

/**
 * Splits a list of items into two groups based on a specified quantity
 * Used for promotions that require exact quantities or have maximum quantity limits
 *
 * @param applicableItems - Array of items that are eligible for the promotion
 * @param exactQuantity - The number of items to include in the first group
 * @param isMaxQtySpecifiedInAction - Flag indicating if this is a maximum quantity limit
 * @returns {Object} Object containing:
 * - slicedItems: First group of items (up to exactQuantity)
 * - remainingItems: Second group of items (items beyond exactQuantity)
 */
function splitItemsByQuantity(
  applicableItems: any[],
  exactQuantity: number,
  isMaxQtySpecifiedInAction: boolean
) {
  // Case 1: Maximum quantity is specified and exact quantity is positive
  // This handles promotions with strict quantity limits
  if (isMaxQtySpecifiedInAction && exactQuantity > 0) {
    const slicedItems = applicableItems.slice(0, exactQuantity);
    const remainingItems = applicableItems.slice(exactQuantity);
    return { slicedItems: slicedItems, remainingItems: remainingItems };
  }
  // Case 2: Exact quantity is positive and less than total items
  // This handles promotions requiring specific quantities
  else if (exactQuantity > 0 && exactQuantity < applicableItems.length) {
    const slicedItems = applicableItems.slice(0, exactQuantity);
    const remainingItems = applicableItems.slice(exactQuantity);
    return { slicedItems: slicedItems, remainingItems: remainingItems };
  }
  // Case 3: No splitting needed
  // Return all items as sliced items with no remaining items
  else {
    return { slicedItems: applicableItems, remainingItems: [] };
  }
}

/**
 * Checks if a promotion is a free item promotion
 * A promotion is considered a free item promotion if:
 * 1. It explicitly gives a free item (GETS_FREE_ITEM)
 * 2. It gives a 100% discount on an item (PERCENTAGE_DISCOUNT_ON_ITEM with value 100)
 *
 * @param promotion - The promotion object to check
 * @returns {boolean} True if the promotion is a free item promotion, false otherwise
 */
function isFreeItemPromotion(promotion: any) {
  if (promotion.coupon_codes && promotion.coupon_codes.length) {
    //for free item coupon codes we are reducing the price of the item to 0 or modifier price
    return false;
  }
  const promotionAction = promotion.action;
  return (
    // Case 1: Direct free item promotion
    promotionAction.type === PROMOTION_ACTIONS.GETS_FREE_ITEM ||
    // Case 2: 100% discount promotion (effectively making the item free)
    ((promotionAction.type === PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ITEM ||
      promotionAction.type === PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT) &&
      promotionAction.value === 100)
  );
}

/**
 * Checks if a promotion is an item-level promotion.
 * @param promotion - The promotion object to check
 * @returns {boolean} True if the promotion is an item-level promotion, false otherwise
 */
function checkIfItemLevelPromotion(promotion: any) {
  const promotionActionType = promotion.action.type;
  const orderLevelPromotionTypes = [
    PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ORDER,
    PROMOTION_ACTIONS.VALUE_DISCOUNT_ON_ORDER,
  ];
  return !orderLevelPromotionTypes.includes(promotionActionType);
}

/**
 * Applies a promotion that requires specific items to be included in the cart.
 * @param promotionAppliedCart - The array of cart items that have already applied promotions.
 * @param promotion - The promotion object.
 * @param orderPromotions - The array of order promotions.
 * @param menuItems - The array of menu items.
 * @param freeItemCart - The array of free items.
 * @returns The array of cart items with the promotion action applied.
 */
function applyBuysSpecificItemsPromotion(
  promotionAppliedCart: any[],
  promotion: any,
  orderPromotions: any[],
  menuItems: any[],
  freeItemCart: any[],
  freeItemsCartCopy: any[]
) {
  let modifiedPromotionAppliedCart = promotionAppliedCart;
  const bundledPromotion =
    promotion.condition?.bundle_discount ||
    promotion.action?.bundle_discount ||
    false;
  let {
    min_qty: minQuantity = 0,
    max_qty: maxQuantity = 0,
    include,
    exclude,
  } = promotion.condition;
  let exactQuantity =
    minQuantity > 0 && minQuantity === maxQuantity ? minQuantity : 0;
  const isFreeItemPromotionType = isFreeItemPromotion(promotion) || false;
  if (isFreeItemPromotionType) {
    //special case for promotions that give a free item
    if (
      (promotion.action.include && !promotion.action.include.length) ||
      promotion.action.include[0].field === 'category_id'
    ) {
      // if the promotion is for a category or no items are included, SO will skip it to prevent errors
      return { modifiedPromotionAppliedCart, orderPromotions };
    }
    const updatedFreeCart = applyFreeItem(
      promotionAppliedCart,
      promotion,
      menuItems,
      freeItemCart,
      freeItemsCartCopy
    );
    modifiedPromotionAppliedCart = [
      ...modifiedPromotionAppliedCart,
      ...updatedFreeCart,
    ];
    return { modifiedPromotionAppliedCart, orderPromotions };
  }

  const applyPromotionIfEligible = (
    conditionIncludeCartItems: any[],
    entireCartIncludeCondition?: boolean
  ) => {
    let allActionItemsIncluded = Boolean(entireCartIncludeCondition);
    if (promotion.action.type === PROMOTION_ACTIONS.PAY_FIXED_PRICE) {
      return applyPromotionAction(
        promotionAppliedCart,
        promotion,
        orderPromotions
      );
    }
    if (allActionItemsIncluded) {
      if (orderPromotions.length > 0) {
        const appliedOrderPromotionPriority = orderPromotions[0].priority;
        const currentPromotionPriority = promotion.priority;
        if (appliedOrderPromotionPriority < currentPromotionPriority) {
          //skip the promotion if the applied order promotion has a higher priority
          return promotionAppliedCart;
        }
      }
      const actionItemsQuantity = promotion.action.quantity ?? 1;
      const newExactQuantity =
        exactQuantity > 0 ? exactQuantity + actionItemsQuantity : 0;
      const newMinQuantity = minQuantity + 1; //action quantity min is always 1
      const newMaxQuantity = maxQuantity + actionItemsQuantity;
      if (
        (newExactQuantity > 0 &&
          newExactQuantity === conditionIncludeCartItems.length) ||
        (newMinQuantity > 0 &&
          newMaxQuantity > 0 &&
          newMinQuantity <= conditionIncludeCartItems.length &&
          newMaxQuantity >= conditionIncludeCartItems.length)
      ) {
        //only exact quantity of items required for promotion or range between min and max quantity
        return applyPromotionAction(
          promotionAppliedCart,
          promotion,
          orderPromotions
        );
      } else if (newMaxQuantity < conditionIncludeCartItems.length) {
        return applyPromotionAction(
          promotionAppliedCart,
          promotion,
          orderPromotions
        );
      }
    } else {
      if (
        (exactQuantity > 0 &&
          exactQuantity === conditionIncludeCartItems.length) ||
        (minQuantity > 0 &&
          maxQuantity > 0 &&
          minQuantity <= conditionIncludeCartItems.length &&
          maxQuantity >= conditionIncludeCartItems.length)
      ) {
        //only exact quantity of items required for promotion or range between min and max quantity
        return applyPromotionAction(
          promotionAppliedCart,
          promotion,
          orderPromotions
        );
      }
      if (
        minQuantity > 0 &&
        maxQuantity === minQuantity &&
        exactQuantity < conditionIncludeCartItems.length
      ) {
        //this is to handle the case where the promotion is for a specific quantity and the cart has more items than the promotion,
        //so we need to apply the promotion to the cart even if condition items exceed the exact quantity
        return applyPromotionAction(
          promotionAppliedCart,
          promotion,
          orderPromotions
        );
      }
      if (
        minQuantity > 0 &&
        maxQuantity > 0 &&
        minQuantity !== maxQuantity &&
        minQuantity < conditionIncludeCartItems.length &&
        orderPromotionTypes.includes(promotion.action.type)
      ) {
        //case where condition items exceed max qty
        return applyPromotionAction(
          promotionAppliedCart,
          promotion,
          orderPromotions
        );
      }
    }

    return promotionAppliedCart;
  };

  if (include.length === 0 && exclude.length === 0) {
    const isItemLevelPromotion = checkIfItemLevelPromotion(promotion);
    modifiedPromotionAppliedCart = applyPromotionIfEligible(
      promotionAppliedCart,
      isItemLevelPromotion
    );
  } else if (include.length && exclude.length === 0) {
    const conditionIncludeItemInCart = findPromotionApplicableItems(
      promotionAppliedCart,
      include
    );
    if (bundledPromotion) {
      const isBundleConditionMet = checkBundledPromotionCondition(
        promotion.condition.include,
        promotionAppliedCart
      );
      if (!isBundleConditionMet) {
        return { modifiedPromotionAppliedCart, orderPromotions };
      }
    }
    if (conditionIncludeItemInCart.length) {
      modifiedPromotionAppliedCart = applyPromotionIfEligible(
        conditionIncludeItemInCart
      );
    } else {
      return { modifiedPromotionAppliedCart, orderPromotions };
    }
  } else if (include.length === 0 && exclude.length) {
    const conditionExcludeItemInCart = findPromotionApplicableItems(
      promotionAppliedCart,
      exclude
    );
    const conditionIncludeItemInCart = promotionAppliedCart.filter(
      (item) => !conditionExcludeItemInCart.includes(item)
    );
    if (conditionIncludeItemInCart.length) {
      modifiedPromotionAppliedCart = applyPromotionIfEligible(
        conditionIncludeItemInCart
      );
    } else {
      return { modifiedPromotionAppliedCart, orderPromotions };
    }
  } else {
    const conditionIncludeItemInCart = findPromotionApplicableItems(
      promotionAppliedCart,
      include
    );
    if (bundledPromotion) {
      const isBundleConditionMet = checkBundledPromotionCondition(
        promotion.condition.include,
        promotionAppliedCart
      );
      if (!isBundleConditionMet) {
        return { modifiedPromotionAppliedCart, orderPromotions };
      }
    }
    const conditionExcludeItemInCart = findPromotionApplicableItems(
      promotionAppliedCart,
      exclude
    );
    const conditionIncludeExcludeItemInCart = conditionIncludeItemInCart.filter(
      (item) => !conditionExcludeItemInCart.includes(item)
    );
    if (conditionIncludeExcludeItemInCart.length) {
      modifiedPromotionAppliedCart = applyPromotionIfEligible(
        conditionIncludeExcludeItemInCart
      );
    } else {
      return { modifiedPromotionAppliedCart, orderPromotions };
    }
  }

  return { modifiedPromotionAppliedCart, orderPromotions };
}

function applyBuysForSpecificAmountPromotion(
  promotionAppliedCart: any[],
  promotion: any,
  orderPromotions: any[],
  menuItems: any[],
  freeItemCart: any[],
  freeItemsCartCopy: any[]
) {
  let { include, exclude, min_price: minPrice } = promotion.condition;
  let modifiedPromotionAppliedCart = promotionAppliedCart;
  const bundledPromotion =
    promotion.condition?.bundle_discount ||
    promotion.action?.bundle_discount ||
    false;

  const applyPromotionIfEligible = (applicableItems: any[]) => {
    const cartSubtotal = findCartSubtotal(applicableItems);
    if (
      promotion.action.type === PROMOTION_ACTIONS.VALUE_DISCOUNT_ON_ITEM ||
      promotion.action.type === PROMOTION_ACTIONS.VALUE_DISCOUNT_ON_ORDER
    ) {
      if (minPrice < promotion.action.value) {
        //if min price is less than promotion value then set min price to promotion value
        minPrice = promotion.action.value;
      }
    }
    if (cartSubtotal >= minPrice) {
      const isFreeItemPromotionType = isFreeItemPromotion(promotion) || false;
      if (isFreeItemPromotionType) {
        if (
          (promotion.action.include && !promotion.action.include.length) ||
          promotion.action.include[0].field === 'category_id'
        ) {
          // if the promotion is for a category or no items are included, SO will skip it to prevent errors
          return modifiedPromotionAppliedCart;
        }
        const updatedFreeCart = applyFreeItem(
          promotionAppliedCart,
          promotion,
          menuItems,
          freeItemCart,
          freeItemsCartCopy
        );
        modifiedPromotionAppliedCart = [
          ...modifiedPromotionAppliedCart,
          ...updatedFreeCart,
        ];
        return modifiedPromotionAppliedCart;
      } else {
        return applyPromotionAction(
          promotionAppliedCart,
          promotion,
          orderPromotions
        );
      }
    }
    return promotionAppliedCart;
  };
  if (include.length === 0 && exclude.length === 0) {
    modifiedPromotionAppliedCart =
      applyPromotionIfEligible(promotionAppliedCart);
  } else if (include.length && exclude.length === 0) {
    const conditionIncludeItemInCart = findPromotionApplicableItems(
      promotionAppliedCart,
      include
    );
    if (bundledPromotion) {
      const isBundleConditionMet = checkBundledPromotionCondition(
        promotion.condition.include,
        promotionAppliedCart
      );
      if (!isBundleConditionMet) {
        return { modifiedPromotionAppliedCart, orderPromotions };
      }
    }
    modifiedPromotionAppliedCart = applyPromotionIfEligible(
      conditionIncludeItemInCart
    );
  } else if (include.length === 0 && exclude.length) {
    const conditionExcludeItemInCart = findPromotionApplicableItems(
      promotionAppliedCart,
      exclude
    );
    const conditionIncludeItemInCart = promotionAppliedCart.filter(
      (item) => !conditionExcludeItemInCart.includes(item)
    );
    modifiedPromotionAppliedCart = applyPromotionIfEligible(
      conditionIncludeItemInCart
    );
  } else {
    const conditionIncludeItemInCart = findPromotionApplicableItems(
      promotionAppliedCart,
      include
    );
    if (bundledPromotion) {
      const isBundleConditionMet = checkBundledPromotionCondition(
        promotion.condition.include,
        promotionAppliedCart
      );
      if (!isBundleConditionMet) {
        return { modifiedPromotionAppliedCart, orderPromotions };
      }
    }
    const conditionExcludeItemInCart = findPromotionApplicableItems(
      promotionAppliedCart,
      exclude
    );
    const conditionIncludeExcludeItemInCart = conditionIncludeItemInCart.filter(
      (item) => !conditionExcludeItemInCart.includes(item)
    );
    modifiedPromotionAppliedCart = applyPromotionIfEligible(
      conditionIncludeExcludeItemInCart
    );
  }

  return { modifiedPromotionAppliedCart, orderPromotions };
}

/**
 * Applies advanced promotions like buy-X-get-Y or conditional discounts
 * @param promotionAppliedCart - Current cart with any previously applied promotions
 * @param promotion - The promotion to apply
 * @param orderPromotions - Array of order-level promotions
 * @param menuItems - Array of all menu items
 * @param freeItemCart - Array of free items
 * @returns Updated cart, order promotions, and free items
 */
function applyAdvancedPromotion(
  promotionAppliedCart: any[],
  promotion: any,
  orderPromotions: any[],
  menuItems: any[],
  freeItemCart: any[],
  freeItemsCartCopy: any[]
) {
  let promotionCondition = promotion.condition;
  let modifiedPromotionAppliedCart = promotionAppliedCart;
  // Handle different types of advanced promotions
  if (
    promotionCondition.type === PROMOTION_CONDITION_TYPES.BUYS_SPECIFIC_ITEMS
  ) {
    // Buy X items, get Y promotion
    return applyBuysSpecificItemsPromotion(
      promotionAppliedCart,
      promotion,
      orderPromotions,
      menuItems,
      freeItemCart,
      freeItemsCartCopy
    );
  } else if (
    promotionCondition.type ===
    PROMOTION_CONDITION_TYPES.BUYS_FOR_SPECIFIC_AMOUNT
  ) {
    // Spend X amount, get Y promotion
    return applyBuysForSpecificAmountPromotion(
      modifiedPromotionAppliedCart,
      promotion,
      orderPromotions,
      menuItems,
      freeItemCart,
      freeItemsCartCopy
    );
  }

  return {
    modifiedPromotionAppliedCart: promotionAppliedCart,
    orderPromotions,
  };
}

/**
 * Finds and applies all eligible promotions to the cart items
 * @param cartItems - Array of items in the cart
 * @param availablePromotions - Array of available promotions to apply
 * @param menuItems - Array of all menu items for reference
 * @param freeItemCart - Array of free items already in cart
 * @returns {Object} Object containing:
 * - appliedPromotions: Array of promotions that were successfully applied
 * - promotionAppliedCart: Updated cart with promotions applied
 * - orderPromotion: Array of order-level promotions
 * - freeItemCart: Updated free items cart
 */
function findAppliedPromotions(
  cartItems: any[],
  availablePromotions: any[],
  menuItems: any[],
  freeItemCart: any[],
  freeItemsCartCopy: any[]
) {
  let orderPromotion: any[] = [];
  let promotionAppliedCart: any[] = cartItems;
  let appliedPromotions: any[] = [];

  // Process each promotion in order of priority
  availablePromotions.forEach((promotion) => {
    if (promotion.promo_type === PROMOTION_TYPES.BASIC) {
      const basicPromotionData = applyBasicPromotion(
        promotionAppliedCart,
        promotion,
        orderPromotion
      );
      promotionAppliedCart = basicPromotionData.promotionAppliedCart;
      orderPromotion = basicPromotionData.orderPromotion;
    } else if (promotion.promo_type === PROMOTION_TYPES.ADVANCED) {
      const advancedPromotionData = applyAdvancedPromotion(
        promotionAppliedCart,
        promotion,
        orderPromotion,
        menuItems,
        freeItemCart,
        freeItemsCartCopy
      );
      // Filter out free items from main cart
      const newFreeItems =
        advancedPromotionData.modifiedPromotionAppliedCart.filter(
          (item) => item.is_free_item
        );
      promotionAppliedCart =
        advancedPromotionData.modifiedPromotionAppliedCart.filter(
          (item) => !item.is_free_item
        );
      const filteredNewFreeItems = newFreeItems.filter(
        (newItem) =>
          !freeItemCart.some(
            (existing) =>
              existing.id === newItem.id &&
              existing.applied_promotion_id === newItem.applied_promotion_id
          )
      );
      freeItemCart = [...freeItemCart, ...filteredNewFreeItems];
      orderPromotion = advancedPromotionData.orderPromotions;
    }
  });

  // Find all promotions that were actually applied to cart items
  appliedPromotions = findCartAppliedPromotions(
    [...promotionAppliedCart, ...freeItemCart],
    availablePromotions
  );

  // Remerge split cart items back together
  promotionAppliedCart = remergeCart(promotionAppliedCart);

  return {
    appliedPromotions,
    promotionAppliedCart,
    orderPromotion,
    freeItemCart,
  };
}

/**
 * @param promotion
 * @param surchargeBreakdown
 * @returns discount Amount
 */
const calculateDeliveryPromotionDiscount = (
  promotion: any,
  surchargeBreakdown: any[]
) => {
  let promotionDiscount = 0;
  let appliableSurchargeId = promotion.action.surcharge.id;
  const deliveryCharge = surchargeBreakdown.find(
    (charge) => charge.id === appliableSurchargeId
  );
  if (deliveryCharge) {
    let surchargeAmount = deliveryCharge.amount;
    const maxDiscount = surchargeAmount;
    switch (promotion.action.type) {
      case PROMOTION_ACTIONS.GETS_FREE_DELIVERY:
        promotionDiscount = maxDiscount;
        break;
      case PROMOTION_ACTIONS.GETS_PERCENTAGE_DISCOUNT_ON_DELIVERY:
        promotionDiscount = (surchargeAmount * promotion.action.value) / 100;
        if (promotionDiscount > maxDiscount) {
          promotionDiscount = maxDiscount;
        }
        break;
      case PROMOTION_ACTIONS.GETS_VALUE_DISCOUNT_ON_DELIVERY:
        promotionDiscount = promotion.action.value;
        if (promotionDiscount > maxDiscount) {
          promotionDiscount = maxDiscount;
        }
        break;
      default:
        break;
    }
  }
  return promotionDiscount;
};

const calculateOrderPromotionDiscount = (
  promotion: any,
  subtotal: number,
  surchargeTotal: number,
  surchargeBreakdown: any[],
  taxTotal: number
) => {
  let discountAmount = 0;
  let discountableAmount = subtotal + surchargeTotal;
  let taxDiscount = 0;
  switch (promotion.action.type) {
    case PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ORDER:
      discountAmount = (discountableAmount * promotion.action.value) / 100;
      const maxDiscount = promotion.action?.max_discount_value ?? 0;
      if (maxDiscount > 0 && discountAmount > maxDiscount) {
        discountAmount = maxDiscount;
      }
      taxDiscount = (taxTotal * promotion.action.value) / 100;
      break;
    case PROMOTION_ACTIONS.VALUE_DISCOUNT_ON_ORDER:
      discountAmount = promotion.action.value;
      const discountRatio = discountAmount / discountableAmount;
      taxDiscount = taxTotal * discountRatio;
      break;
    case PROMOTION_ACTIONS.GETS_PERCENTAGE_DISCOUNT_ON_DELIVERY:
    case PROMOTION_ACTIONS.GETS_VALUE_DISCOUNT_ON_DELIVERY:
    case PROMOTION_ACTIONS.GETS_FREE_DELIVERY:
      discountAmount = calculateDeliveryPromotionDiscount(
        promotion,
        surchargeBreakdown
      );
    default:
      break;
  }
  const discountRatio = discountAmount / discountableAmount;
  taxDiscount = taxTotal * discountRatio;
  return { discountAmount, taxDiscount, discountRatio };
};

/**
 * Checks if an item in the cart has an applied promotion.
 * @param cartItem - The cart item to check.
 * @param promotionAppliedCart - The array of cart items with applied promotions.
 * @returns applied promotion item else 0
 */
const checkItemHasAppliedPromotion = (
  cartItem: any,
  promotionAppliedCart: any[]
) => {
  const appliedPromotion = promotionAppliedCart.find(
    (item) =>
      item.cart_id === cartItem.cart_id &&
      item.applied_promotion_id !== undefined
  );
  return appliedPromotion ? appliedPromotion.applied_promotion_id : 0;
};

/**
 * Filters out delivery promotions from the given array of promotions.
 * @param promotions - The array of promotions.
 * @returns The filtered array of promotions.
 */
function filterOutDeliveryPromotions(promotions: any[]) {
  return promotions.filter(
    (promotion) =>
      promotion.action.type !== PROMOTION_ACTIONS.GETS_FREE_DELIVERY &&
      promotion.action.type !==
        PROMOTION_ACTIONS.GETS_PERCENTAGE_DISCOUNT_ON_DELIVERY &&
      promotion.action.type !==
        PROMOTION_ACTIONS.GETS_VALUE_DISCOUNT_ON_DELIVERY
  );
}

/**
 * Transforms the order promotion to Discount object
 * @param orderPromotion - The order promotion object.
 * @returns The Discount object.
 */
function transformOrderPromotionToDiscount(
  orderPromotion: any,
  subTotal: string,
  surchargeTotal: string,
  surchargesBreakdown: any[]
) {
  const isDeliveryPromotion = orderPromotion.action.surcharge;
  const orderSubTotal = parseFloat(subTotal);
  const orderSurchageTotal = parseFloat(surchargeTotal);
  const isPercentageDiscount =
    orderPromotion.action.type ===
    PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ORDER;
  const orderPromotionDiscount = calculateOrderPromotionDiscount(
    orderPromotion,
    orderSubTotal,
    orderSurchageTotal,
    surchargesBreakdown,
    0 // no need to calculate tax discount for order promotion here
  );
  const promotionRate = orderPromotion.action.value;
  const discountAmount = orderPromotionDiscount.discountAmount;
  const isPromoCappedDiscount =
    isPercentageDiscount &&
    orderPromotion.action.max_discount_value == discountAmount;
  return {
    name: orderPromotion.name,
    type: isPercentageDiscount ? DISCOUNT_TYPE.PERCENTAGE : DISCOUNT_TYPE.VALUE,
    discount_type: isPercentageDiscount ? (isPromoCappedDiscount ? 2 : 1) : 2, //1 is percentage and 2 is value
    on_total: true,
    is_capped: isPromoCappedDiscount,
    rate: isPromoCappedDiscount ? discountAmount : promotionRate,
    amount: discountAmount,
    movement_type: orderPromotion.action.movement_type ?? 'sold',
    promotion_id: orderPromotion.id,
  };
}

const getDiscountType = (promotionActionType: PROMOTION_ACTIONS) => {
  const isPercentageDiscount = [
    PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT,
    PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ITEM,
    PROMOTION_ACTIONS.PERCENTAGE_DISCOUNT_ON_ORDER,
  ].includes(promotionActionType);
  return isPercentageDiscount ? 'percentage' : 'value';
};

/**
 * Transforms an item promotion to a discount object.
 * @param itemPromotion - The item promotion object.
 * @param item - The item object.
 * @returns The discount object.
 */
function transformItemPromotionToDiscount(itemPromotion: any, item: any) {
  const subTotalBeforePromotion = item.itemTotalBeforePromotion * item.count;
  if (subTotalBeforePromotion) {
    const discountType = getDiscountType(itemPromotion.action.type);
    const discountAmount = subTotalBeforePromotion - item.sub_total;
    const promotionRate = itemPromotion.action.value;
    return {
      name: itemPromotion.name,
      type: DISCOUNT_TYPE.VALUE,
      rate: discountType === "percentage" ? promotionRate : discountAmount,
      amount: discountAmount,
      promotion_id: itemPromotion.id,
      movement_type: itemPromotion.action.movement_type ?? 'sold',
      discount_type:
        discountType === 'percentage'
          ? DISCOUNT_TYPE.PERCENTAGE
          : DISCOUNT_TYPE.VALUE, //POS display
    };
  }
  return null;
}

/**
 * Transforms a free item promotion to a discount object.
 * @param itemPromotion - The item promotion object.
 * @param item - The item object.
 * @returns The discount object.
 */
function transformFreeItemPromotionToDiscount(itemPromotion: any, item: any) {
  const itemSubTotalBeforePromotion =
    item.itemTotalBeforePromotion ?? item.unit_price;
  const discountAmount = itemSubTotalBeforePromotion - item.sub_total;
  return {
    discount_type: DISCOUNT_TYPE.PERCENTAGE,
    name: itemPromotion.name,
    type: DISCOUNT_TYPE.VALUE,
    rate: 100,
    amount: discountAmount,
    promotion_id: itemPromotion.id,
    movement_type: itemPromotion.action.movement_type ?? 'sold',
  };
}

function removeUnavailablePromotionId(cartItems: any[], promotions: []) {
  let updatedCartItems: any[] = cartItems.map((item: any) => {
    // Guard against null/undefined items
    if (!item) {
      return item;
    }
    if (item.applied_promotion_id) {
      let appliedPromotionId = item.applied_promotion_id;
      let isPromotionAvailable: any = promotions.find(
        (promotion: any) => promotion.id === appliedPromotionId
      );
      if (isPromotionAvailable && isPromotionAvailable?.coupon_codes) {
        isPromotionAvailable = null;
      }
      if (
        (!isPromotionAvailable && item?.applied_promotion_id) ||
        item?.item?.applied_promotion_id
      ) {
        if (item?.applied_promotion_id) {
          delete item.applied_promotion_id;
        }
        if (item?.item?.applied_promotion_id) {
          delete item.item.applied_promotion_id;
        }
      }
      return item;
    } else {
      return item;
    }
  });
  return updatedCartItems;
}

function removePromotions(pastOrderItems: any, payloadItems: any) {
  let transformedItems: any[] = [];
  if (pastOrderItems.length) {
    transformedItems = pastOrderItems.map((item: any) => {
      if (item.applied_promotion_id) {
        //removing the changes done in cartItems for promotions
        delete item.applied_promotion_id;
        delete item.applied_promotion_priority;
        item.is_free_item = false;
        let itemPayloadData = payloadItems.find(
          (payloaditem: any) => payloaditem.id === item.id
        );
        if (itemPayloadData) {
          item.sub_total = itemPayloadData.sub_total;
        }
      }
      return item;
    });
  }
  return transformedItems;
}

/**
 * Transforms a free item with a modifier to a valid item with a calculated sub_total.
 * @param freeItem - A free item with a modifier.
 * @returns A valid item with a calculated sub_total.
 */
const transformModifierAppliedFreeItem = (freeItem: any) => {
  let updatedItem = { ...freeItem };
  updatedItem.sub_total = calculateSubTotal(updatedItem);
  const itemSubtotal = updatedItem.sub_total;
  return {
    ...updatedItem,
    subtotalBeforePromotion: itemSubtotal,
    sub_total: updatedItem.promotionIncludesModifier
      ? 0
      : itemSubtotal - updatedItem.unit_price,
  };
};

/**
 * Combine the promotion applied cart and the free items cart.
 * @param promotionAppliedCart - The cart with items that have promotions applied.
 * @param freeItemsCart - The cart with free items.
 * @returns The combined cart.
 */
const combineCarts = (
  promotionAppliedCart: any[],
  freeItemsCart: any[]
): any[] => {
  return promotionAppliedCart.concat(freeItemsCart);
};

/**
 * Validates if an old free item can be reused instead of creating a new one
 * Used to prevent creating new free items when the old one is still valid with modifiers so users dont have to select the free item again
 * Old item is item stored in local storage and new item is item generated from promotion calculation
 * @param oldFreeItem - The existing free item in the cart
 * @param newFreeItem - The newly generated free item
 * @returns boolean indicating if the old item is valid and matches the new item
 */
const checkValidOldFreeItem = (oldFreeItem: any, newFreeItem: any): boolean => {
  // Early return if either item is missing
  if (!oldFreeItem || !newFreeItem) {
    return false;
  }

  // Check if items are the same (matching id and name)
  const isSameItem =
    oldFreeItem.id.toString() === newFreeItem.id.toString() &&
    oldFreeItem.name === newFreeItem.name;

  // Check if old item has valid subtotals
  const hasValidSubtotals =
    typeof oldFreeItem.itemTotalBeforePromotion === 'number' &&
    oldFreeItem.itemTotalBeforePromotion >= 0 &&
    typeof oldFreeItem.sub_total === 'number' &&
    oldFreeItem.sub_total >= 0;

  return isSameItem && hasValidSubtotals;
};

//prevent modifers data reset
const checkAndUpdateFreeCart = (oldFreeCart: any[], newFreeCart: any[]) => {
  let updatedNewCart: any[] = [];
  if (oldFreeCart.length === newFreeCart.length) {
    for (let i = 0; i < newFreeCart.length; i++) {
      const isValidOldFreeItem = checkValidOldFreeItem(
        oldFreeCart[i],
        newFreeCart[i]
      );
      if (isValidOldFreeItem) {
        updatedNewCart.push(oldFreeCart[i]);
      } else {
        updatedNewCart.push(newFreeCart[i]);
      }
    }
  } else {
    updatedNewCart = newFreeCart;
  }
  return updatedNewCart;
};

/**
 * Split the promotions array into two arrays.
 * One array contains promotions with promo codes and the other array contains promotions without promo codes.
 * @param promotions - The array of promotions.
 * @returns Two arrays. The first array contains promotions with promo codes and the second array contains promotions without promo codes.
 */
const splitPromoCodes = (
  promotions: any[],
  appliedPromoCode: string
): { promotionWithPromoCodes: any[]; promotionWithoutPromoCodes: any[] } => {
  return promotions.reduce(
    (result, promotion) => {
      const hasCouponCodes = promotion.coupon_codes?.length > 0;
      const hasMatchingCode =
        hasCouponCodes &&
        appliedPromoCode && // Explicitly check that appliedPromoCode is truthy
        appliedPromoCode.trim() !== '' && // Ensure it's not just whitespace
        promotion.coupon_codes.some(
          (coupon: { code: string }) => coupon.code === appliedPromoCode
        );

      // All promotions without coupon codes go to promotionWithoutPromoCodes
      if (!hasCouponCodes) {
        result.promotionWithoutPromoCodes.push(promotion);
        return result;
      }

      // Promotions with coupon codes always go to promotionWithPromoCodes
      result.promotionWithPromoCodes.push(promotion);

      // If there's a matching code, also add to promotionWithoutPromoCodes
      if (hasMatchingCode) {
        result.promotionWithoutPromoCodes.push(promotion);
      }

      return result;
    },
    {
      promotionWithPromoCodes: [],
      promotionWithoutPromoCodes: [],
    }
  );
};

/**
 * Filters out unappliable order promotions from the given array of promotions.
 * @param promotions - The array of promotions.
 * @param currentCartSubTotal - The current cart subtotal.
 * @returns The filtered array of promotions.
 */
const filterOutUnAppliableOrderPromotions = (
  promotions: any[],
  currentCartSubTotal: number
) => {
  return promotions.filter((promotion) => {
    const isOrderPromotion =
      promotion.action.type === PROMOTION_ACTIONS.VALUE_DISCOUNT_ON_ORDER;
    if (isOrderPromotion) {
      const discountAmount = promotion.action.value;
      if (currentCartSubTotal >= discountAmount) {
        return promotion;
      } else {
        return;
      }
    }
    return promotion;
  });
};

/**
 * Filters out promotions that include out of stock items or items not in the menu.
 * @param promotions - The array of promotions to filter.
 * @param outOfStockItems - The array of out of stock items.
 * @param menuItems - The array of menu items.
 * @returns The filtered array of promotions.
 */
const filterFreePromotions = (
  promotions: any[],
  outOfStockItems: any[],
  menuItems: any[]
) => {
  return promotions.filter((promotion) => {
    // Check if it's a free item promotion
    if (isFreeItemPromotion(promotion)) {
      // Check if promotion has action include items
      if (promotion.action.include && promotion.action.include.length > 0) {
        // Check if any of the included items are out of stock or not in menu
        const hasInvalidItem = promotion.action.include.some(
          (item: any) =>
            item.field === 'item_id' &&
            (outOfStockItems.includes(item.id) ||
              !menuItems.some(
                (menuItem: any) => menuItem.id === parseInt(item.id)
              ))
        );
        // Return false if any included item is out of stock or not in menu
        return !hasInvalidItem;
      }
    }
    // Return true for non-free promotions
    return true;
  });
};

/**
 * Sorts the cart items by the last updated at timestamp.
 * @param cartItems - The array of cart items.
 * @returns The sorted array of cart items.
 */
const sortCartByLastUpdatedAt = (cartItems: any[]) => {
  return cartItems.sort((a, b) => {
    if (!a.last_updated_at && !b.last_updated_at) return 0; // If both items have no last_updated_at, keep them in original order
    if (!a.last_updated_at) return 1; // If a has no last_updated_at, move it to the end
    if (!b.last_updated_at) return -1; // If b has no last_updated_at, move it to the end
    return a.last_updated_at - b.last_updated_at; // Sort by last_updated_at in ascending order
  });
};

/**
 * Checks if the promo code is appliable for the current cart items.
 * @param promotions - The array of promotions.
 * @param cart - The array of cart items.
 * @param menu - The array of menu items.
 * @param freeItems - The array of free items.
 * @param freeItemCartCopy - The array of free item cart items.
 * @returns The array of promotions with the coupon codes applied.
 */
const checkPromoCodeAppliable = (
  promotions: any[],
  cart: any[],
  menu: any[],
  freeItems: any[],
  freeItemCartCopy: any[]
) => {
  return promotions.map((promotion) => {
    // Get applied promotions for this specific promotion
    const promotionData = findAppliedPromotions(
      cart,
      [promotion],
      menu,
      freeItems,
      freeItemCartCopy
    );
    const appliedPromotions = promotionData.appliedPromotions || [];
    const orderPromotions = promotionData.orderPromotion || [];

    // For order-level promotions, check if it's in orderPromotions
    const isOrderPromotion =
      promotion.action.type === 'gets_percent_discount_on_order' ||
      promotion.action.type === 'gets_value_discount_on_order';

    // Check if this promotion was successfully applied
    const appliedPromotion = isOrderPromotion
      ? orderPromotions.find(
          (op) => op.id.toString() === promotion.id.toString()
        )
      : appliedPromotions.find(
          (ap) => ap.id.toString() === promotion.id.toString()
        );

    // For order promotions, also check if cart meets minimum amount
    let isApplicable = Boolean(appliedPromotion);
    if (
      isOrderPromotion &&
      promotion.condition.type === 'buys_for_specific_amount'
    ) {
      const cartTotal = cart.reduce((total, item) => total + item.sub_total, 0);
      isApplicable = cartTotal >= promotion.condition.min_price;
    }

    // Return promotion with updated coupon codes
    return {
      ...promotion,
      coupon_codes: promotion.coupon_codes.map((code: any) => ({
        ...code,
        condition_fullfilled: isApplicable,
      })),
    };
  });
};

/**
 * Gets the coupon data for the promo code.
 * @param promoCode - The promo code.
 * @param availablePromoCodes - The array of available promotions.
 * @returns The coupon data.
 */
const getCouponData = (promoCode: string, availablePromoCodes: any[]) => {
  const normalizedPromoCode = (promoCode || '').trim().toLowerCase();
  const appliedPromotion = availablePromoCodes.find((promotion: any) =>
    promotion.coupon_codes?.some(
      (couponCode: { code: string }) =>
        (couponCode.code || '').trim().toLowerCase() === normalizedPromoCode
    )
  );
  if (!appliedPromotion) {
    return null;
  }
  const matchedCoupon = appliedPromotion?.coupon_codes?.find(
    (couponCode: { code: string }) =>
      (couponCode.code || '').trim().toLowerCase() === normalizedPromoCode
  );
  return {
    appliedPromotion,
    matchedCoupon,
  };
};

/**
 * Gets the verification payload for the promo code.
 * @param promoCode - The promo code.
 * @param availablePromoCodes - The array of available promotions.
 * @param sapaadCompanyId - The id of the company.
 * @param sapaadCustomerId - The id of the customer.
 * @param skipApplicabilityCheck - Optional flag to skip the applicability check (useful when clearing reservations).
 * @returns The verification payload.
 */
const getPromoCodeVerificationPayload = (
  promoCode: string,
  availablePromoCodes: any[],
  sapaadCompanyId: string,
  sapaadCustomerId: string,
  skipApplicabilityCheck?: boolean
) => {
  const couponData = getCouponData(promoCode, availablePromoCodes);
  const appliedPromotion = couponData?.appliedPromotion;
  const matchedCoupon = couponData?.matchedCoupon;
  const isPromoCodeAppliable = Boolean(matchedCoupon?.condition_fullfilled);
  // Return null if promotion/coupon not found, or if applicability check fails (unless skipped)
  if (
    !appliedPromotion ||
    !matchedCoupon ||
    (!skipApplicabilityCheck && !isPromoCodeAppliable)
  ) {
    return null;
  }
  return {
    code: promoCode,
    company_id: sapaadCompanyId,
    customer_id: sapaadCustomerId,
    promotion_id: appliedPromotion.id,
    promo_code_id: matchedCoupon?.id,
  };
};

/**
 * Checks if the promo code has a redeem limit.
 * @param promoCode - The promo code.
 * @param availablePromoCodes - The array of available promotions.
 * @returns True if the promo code has a redeem limit, false otherwise.
 */
const promoCodeHasRedeemLimit = (
  promoCode: any,
  availablePromoCodes: any[]
) => {
  const couponData = getCouponData(promoCode, availablePromoCodes);
  const matchedCoupon = couponData?.matchedCoupon;
  const hasRedeemLimit =
    matchedCoupon?.code_redemption_limit > 0 ||
    matchedCoupon?.user_redemption_limit > 0;
  //if either of the redemption limits is greater than 0, then the promo code has a redeem limit
  return hasRedeemLimit;
};

/**
 * Checks if an item should be duplicated based on the promotions.
 * @param item - The item to check.
 * @param promotions - The array of promotions.
 * @returns True if the item should be duplicated, false otherwise.
 * This is done to maintain consitencey with the POS system.
 */
const checkItemIsActionItem = (item: any, promotions: any[]) => {
  if (promotions.length === 0) return false;

  const itemMatches = (actionEntry: any) => String(actionEntry.id) === String(item.id);

  return promotions.some((promotion) => {
    const { include = [], exclude = [] } = promotion.action || {};
    const promotionActionType = promotion.action?.type;
    const isPromotionOrderLevel = orderPromotionTypes.includes(promotionActionType);

    // No include/exclude and not order-level: all items are action items
    if (include.length === 0 && exclude.length === 0 && !isPromotionOrderLevel) return true;

    // Include selected: item must be in include (exclude is not used when include is selected)
    if (include.length > 0) return include.some(itemMatches);

    // Exclude selected: item must not be in exclude
    return exclude.length > 0 && !exclude.some(itemMatches);
  });
};


export {
  appendCategoryDetails,
  duplicateCartItems,
  findCartSubtotal,
  sortPromotions,
  findAppliedPromotions,
  calculateOrderPromotionDiscount,
  checkItemHasAppliedPromotion,
  filterOutDeliveryPromotions,
  transformOrderPromotionToDiscount,
  transformItemPromotionToDiscount,
  removeUnavailablePromotionId,
  removePromotions,
  transformModifierAppliedFreeItem,
  transformFreeItemPromotionToDiscount,
  combineCarts,
  checkAndUpdateFreeCart,
  splitPromoCodes,
  filterOutUnAppliableOrderPromotions,
  filterFreePromotions,
  sortCartByLastUpdatedAt,
  checkPromoCodeAppliable,
  getCouponData,
  getPromoCodeVerificationPayload,
  promoCodeHasRedeemLimit,
  checkItemIsActionItem,
};
